import { useCallback } from "react";
import { Link, useLocation } from 'react-router-dom';
import { MENU_TENANT_MAIN } from "@/config/layout.config";
import { MenuConfig } from "@/config/types";
import { normalizePathname } from "@/lib/normalizePathname";
import {
  AccordionMenu,
  AccordionMenuGroup,
  AccordionMenuItem,
  AccordionMenuLabel,
} from '@marine/ui/accordion-menu';
import { Badge } from '@marine/ui/badge';

interface SidebarPrimaryMenuProps {
  menuConfig?: MenuConfig;
}

export function SidebarPrimaryMenu({ menuConfig = MENU_TENANT_MAIN }: SidebarPrimaryMenuProps) {
  const { pathname } = useLocation();

  // Memoize matchPath to prevent unnecessary re-renders. Compare with trailing
  // slashes normalized so `/crm` matches a `/crm/` location.
  const normalizedPathname = normalizePathname(pathname);
  const matchPath = useCallback(
    (path: string): boolean => normalizePathname(path) === normalizedPathname,
    [normalizedPathname],
  );

  return (
    <AccordionMenu
      selectedValue={pathname}
      matchPath={matchPath}
      type="multiple"
      className="space-y-7.5 px-2.5"
      classNames={{
        label: 'text-xs font-normal text-muted-foreground mb-2',
        item: 'h-8.5 px-2.5 text-sm font-normal text-foreground hover:text-link data-[selected=true]:bg-muted data-[selected=true]:text-foreground [&[data-selected=true]_svg]:opacity-100',
        group: '',
      }}
    >
      {menuConfig.map((item, index) => {
        return (
          <AccordionMenuGroup key={index}>
            {item.title && (
              <AccordionMenuLabel>
                {item.title}
              </AccordionMenuLabel>
            )}
            {item.children?.map((child, childIndex) => {
              return (
                <AccordionMenuItem key={childIndex} value={child.path || '#'}>
                  <Link to={child.path || '#'}>
                    {child.icon && <child.icon />}
                    <span>{child.title}</span>
                    {child.badge === 'Beta' && <Badge size="sm" variant="destructive" appearance="light">{child.badge}</Badge>}
                  </Link>
                </AccordionMenuItem>
              )
            })}
          </AccordionMenuGroup>
        )
      })}
    </AccordionMenu>
  );
}